// Shared helper functions for Tracking components

import { Contract, ContractSummary } from '../../../services/contractApi';
import { WorkflowStatus } from './TrackingTypes';

// Helper function to convert API WorkflowStatus to local WorkflowStatus
export const convertWorkflowStatus = (apiStatus: import('../../../services/contractApi').WorkflowStatus | null): WorkflowStatus | null => {
  if (!apiStatus) return null;
  
  const validWorkflowSteps = ['check_in', 'payment', 'contract_confirmation', 'key_handover', 'completed'] as const;
  const validCheckInStatuses = ['not_started', 'in_progress', 'pending_confirmation', 'completed', 'cancelled'] as const;
  const validKeyHandoverStatuses = ['pending', 'in_progress', 'completed', 'cancelled'] as const;
  
  console.log('[convertWorkflowStatus] API key_handover_status:', apiStatus.key_handover_status, 'is valid:', validKeyHandoverStatuses.includes(apiStatus.key_handover_status as any));
  
  return {
    ...apiStatus,
    workflow_scenario: apiStatus.workflow_scenario === 'checkin_first' || apiStatus.workflow_scenario === 'payment_first'
      ? apiStatus.workflow_scenario
      : undefined,
    workflow_current_step: apiStatus.workflow_current_step && validWorkflowSteps.includes(apiStatus.workflow_current_step as any)
      ? apiStatus.workflow_current_step as typeof validWorkflowSteps[number]
      : undefined,
    check_in_status: apiStatus.check_in_status && validCheckInStatuses.includes(apiStatus.check_in_status as any)
      ? apiStatus.check_in_status as typeof validCheckInStatuses[number]
      : undefined,
    key_handover_status: apiStatus.key_handover_status && validKeyHandoverStatuses.includes(apiStatus.key_handover_status as any)
      ? apiStatus.key_handover_status as typeof validKeyHandoverStatuses[number]
      : undefined
  };
};

// Helper function để kiểm tra contract đã ký đầy đủ (xét landlord)
export const isContractFullySigned = (contract: Contract | null | undefined): boolean => {
  if (!contract) return false;
  
  // Nếu có landlord (Tenant-Landlord mode): cần cả tenant_signed và landlord_signed
  // Nếu không có landlord (Tenant-Broker mode): chỉ cần tenant_signed
  const hasLandlord = !!contract.landlord_id;
  
  if (hasLandlord) {
    return contract.tenant_signed === true && contract.landlord_signed === true;
  } else {
    // Broker-only: chỉ cần tenant ký
    return contract.tenant_signed === true;
  }
};

/**
 * Parse số tiền từ API (number | string). Xử lý nhóm kiểu VN (1.000 = 1000), bỏ đ/₫/khoảng trắng.
 * Tránh parseFloat("1.000") === 1 làm sai tiền cọc / tổng bước thanh toán.
 */
export function parseContractMoney(value: unknown): number {
  if (value == null || value === '') return 0;
  if (typeof value === 'number') return Number.isFinite(value) ? value : 0;

  let s = String(value).trim();
  if (!s) return 0;

  s = s.replace(/[đ₫]/gi, '').replace(/\s+/g, '').trim();
  s = s.replace(/d$/i, '');

  const normalized = s.replace(/[^\d.,-]/g, '');
  if (!normalized || normalized === '-') return 0;

  const hasComma = normalized.includes(',');
  const hasDot = normalized.includes('.');

  if (hasComma && hasDot) {
    const lastComma = normalized.lastIndexOf(',');
    const lastDot = normalized.lastIndexOf('.');
    if (lastComma > lastDot) {
      return parseFloat(normalized.replace(/\./g, '').replace(',', '.')) || 0;
    }
    return parseFloat(normalized.replace(/,/g, '')) || 0;
  }

  if (hasDot && !hasComma) {
    const parts = normalized.split('.');
    if (parts.length > 1 && parts[parts.length - 1]!.length === 3 && /^\d+$/.test(parts[parts.length - 1]!)) {
      return parseInt(parts.join(''), 10) || 0;
    }
    const n = parseFloat(normalized);
    return Number.isFinite(n) ? n : 0;
  }

  if (hasComma && !hasDot) {
    const parts = normalized.split(',');
    if (parts.length > 1 && parts[parts.length - 1]!.length === 3 && /^\d+$/.test(parts[parts.length - 1]!)) {
      return parseInt(parts.join(''), 10) || 0;
    }
    const n = parseFloat(normalized.replace(',', '.'));
    return Number.isFinite(n) ? n : 0;
  }

  const n = parseFloat(normalized);
  return Number.isFinite(n) ? n : 0;
}

/**
 * Tiền cọc hợp đồng tính bằng VND.
 * Dữ liệu cũ/sai: property lưu "1" = 1 tháng nhưng API/Pydantic có thể thành số 1 → DB 1đ.
 * Nếu giá trị là số nguyên 1–12 và < 1000, coi là số tháng cọc × giá thuê/tháng (đồng bộ logic backend parse_deposit).
 */
export function resolveContractDepositVnd(
  contract: Contract | ContractSummary | null | undefined
): number {
  if (!contract) return 0;
  const monthly = parseContractMoney(contract.monthly_payment);
  const raw = parseContractMoney(contract.deposit);
  if (raw <= 0) return 0;
  const n = Math.round(raw);
  const isIntegerLike = Math.abs(raw - n) < 1e-6;
  if (monthly > 0 && isIntegerLike && n >= 1 && n <= 12 && raw < 1000) {
    return monthly * n;
  }
  return raw;
}

/** Tiền thuê kỳ đầu (không gồm cọc, không phí nền tảng) — đồng bộ với bước 3 Tracking */
export function calculateFirstPeriodRent(contract: Contract | ContractSummary | null | undefined): number {
  if (!contract) return 0;

  let durationMonths = 0;
  if (contract.start_date && contract.end_date) {
    const start = new Date(contract.start_date);
    const end = new Date(contract.end_date);
    const diffTime = Math.abs(end.getTime() - start.getTime());
    durationMonths = Math.max(1, Math.floor(diffTime / (1000 * 60 * 60 * 24 * 30)));
  }

  let monthsPerPeriod =
    contract.payment_term && contract.payment_term > 0 ? contract.payment_term : 3;
  if (contract.total_payments && contract.total_payments > 1 && durationMonths > 0) {
    monthsPerPeriod = Math.floor(durationMonths / contract.total_payments);
    if (monthsPerPeriod < 1) monthsPerPeriod = 1;
  } else if (durationMonths > 0) {
    monthsPerPeriod = durationMonths;
  }

  const monthly = parseContractMoney(contract.monthly_payment);
  if (!monthly) return 0;

  if (contract.total_payments && contract.total_payments > 1 && durationMonths >= monthsPerPeriod) {
    return monthly * monthsPerPeriod;
  }
  if (durationMonths > 0 && durationMonths < monthsPerPeriod) {
    return monthly * durationMonths;
  }
  if (contract.total_payments === 1 && contract.amount != null) {
    return parseContractMoney(contract.amount);
  }
  return monthly;
}

// Helper function để format date với giờ
export const formatDateTime = (dateString: string | Date | null | undefined): string => {
  if (!dateString) return '';
  try {
    const date = typeof dateString === 'string' ? new Date(dateString) : dateString;
    if (isNaN(date.getTime())) return '';
    
    const day = String(date.getDate()).padStart(2, '0');
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const year = date.getFullYear();
    const hours = String(date.getHours()).padStart(2, '0');
    const minutes = String(date.getMinutes()).padStart(2, '0');
    
    return `${day}/${month}/${year} - ${hours}:${minutes}`;
  } catch {
    return '';
  }
};

// Helper function to calculate total required payment (deposit + first period payment + platform fee)
// Tính toán ĐÚNG: first_period_payment = monthly_payment × months_per_period
export const calculateTotalRequired = (contract: Contract | ContractSummary | null | undefined): number => {
  if (!contract) return 0;

  const depositAmount = resolveContractDepositVnd(contract);
  const firstPeriodPayment = calculateFirstPeriodRent(contract);

  // Platform fee (5,000 VND for testing) - payments đã bao gồm platform fee
  const PLATFORM_FEE = 5000; // TEMP: Changed to 5000 VND for SePay testing (min bank limit)

  return depositAmount + firstPeriodPayment + PLATFORM_FEE;
};

